import { Package } from 'lucide-react';

import { useOrders } from '@/lib/api/hooks';
import { PageHeader } from '@/components/app-shell';
import { useSupplementOrders } from '@/features/marketplace/supplements/api';
import { SupplementOrders } from '@/features/marketplace/supplements/SupplementOrders';
import { OrderRow } from './components/OrderRow';
import { LabOrdersSummary } from '@/features/labs/LabOrdersScreen';
import { useLabOrders } from '@/lib/api/labHooks';

/**
 * OrdersScreen — patient's order list.
 *
 * PageHeader (mono-caps headline + arrow-indent description) on top,
 * order rows below. Empty state renders a quiet placeholder; loading
 * collapses to a skeleton band.
 */
export function OrdersScreen() {
  const { data, isLoading } = useOrders();
  const { data: supplementData } = useSupplementOrders();
  const { data: labData } = useLabOrders();
  if (isLoading)
    return <div className="h-[20rem] bg-[var(--surface-sunken)] animate-pulse" />;
  // Cancelled orders sink to the bottom (stable sort preserves the API's
  // recency ordering within each group) — mirrors SubscriptionsSection.
  const orders = [...(data ?? [])].sort(
    (a, b) =>
      Number(a.status === 'cancelled') - Number(b.status === 'cancelled'),
  );
  const count = (orders.length + (supplementData?.orders.length ?? 0) + (labData?.orders.length ?? 0)).toString().padStart(2, '0');

  return (
    <div className="flex flex-col">
      <PageHeader
        title={
          <span className="inline-flex items-baseline gap-10">
            Orders
            <sup className="font-mono text-12 text-[#7C7C7C]">{count}</sup>
          </span>
        }
        description="Use your orders panel to monitor the status of current and past orders."
      />

      <div className="flex flex-col px-15 md:px-40 py-25 gap-15 max-w-[65rem] mx-auto w-full">
        <LabOrdersSummary />
        <SupplementOrders />
        {orders.length === 0 ? (
          <div className="border border-[#ECECEC]">
            <div className="flex items-center justify-between px-15 py-10 bg-[var(--surface-sunken)]">
              <Package
                size={20}
                strokeWidth={1.5}
                className="mx-auto text-[#7C7C7C]"
              />
              <div className="flex-1 font-mono text-13 uppercase text-[#7C7C7C] text-right">
                No prescription orders yet
              </div>
            </div>
            <div className="font-body italic border-t border-[#ECECEC] text-13 text-[#7C7C7C] max-w-[44ch] mx-auto leading-135 p-15">
              When your clinician approves a refill, your order will appear here.
            </div>
          </div>
        ) : (
          orders.map((o) => <OrderRow key={o.id} order={o} />)
        )}
      </div>
    </div>
  );
}
